import { nanoid } from 'nanoid';
import type {
  ProjectData,
  BookPageData,
  ProjectConfigData,
  KieStoryPage,
  ActionData,
  PageSide,
  ElementConfig,
  KieStoryTTSData,
  TTSAudioData
} from '@/types';
import {
  ACTION_TYPE,
  type LayoutType,
  DEFAULT_PAGE_DURATION,
  LAYOUT_TYPE,
  TTS_AUDIO_LEADING_PAUSE_DURATION,
  TTS_AUDIO_TRAILING_PAUSE_DURATION,
  DEFAULT_ENABLE_WATERMARK
} from '@/constants';
import {
  getLayoutConfig,
  applyCoverTextAdaptive,
  normalizePageAspectRatio,
  parsePageDimensions,
  resolveLayoutType
} from './layoutConfig';
import { getBook, updateBook } from '@/api/book';
import { decryptProjectConfig } from '@/utils/decrypt';
import { getAudioDurationFromUrl } from '@/utils/audioMeta';
import { createImageActionData, createSoundActionData, createTextActionData } from './dataTemplate';
import { useProjectStore, useUserStore } from '@/store';
import { getAbsolutePath, getTTSAudioMetaList, hydrateProjectTTSAudioSplits } from '@/utils';
import { fontManager } from '@/managers/FontManager';
import { KieStoryTailPageDataBuilder } from './KieStoryTailPageDataBuilder';
import { applyPublishedUiConfig, fetchPublishedProjectConfig } from './readerProjectLoader';

export async function fetchProjectData(projectId?: string) {
  let data: ProjectConfigData;

  if (projectId) {
    data = await fetchKieStoryAsProjectData(projectId as string);
  } else {
    data = await fetchLocalProjectConfig();
  }

  return data;
}

/** 阅读入口分流结果：已转存打开发布目录，未转存走预览弹窗 */
export type PrepareBookForReadResult = {
  mode: 'published' | 'preview';
  data: ProjectConfigData;
};

/** 从远程链接拉取 kie-gpt 生成的故事 json，并解析为 ProjectData */
export async function fetchKieStoryAsProjectData(projectId: string): Promise<ProjectConfigData> {
  const { data, needUpdate } = await loadAndParseKieStory(projectId);

  // 步骤：尽量写回；图片生成中软失败，编辑器打开仍可用内存数据
  if (needUpdate) {
    await tryPersistProjectConfig(projectId, data);
  }

  return data;
}

/**
 * 阅读入口：解析后尝试写回。
 * update 成功 / 无需写回 → published；IMAGE_GENERATING → preview。
 */
export async function prepareBookForRead(projectId: string): Promise<PrepareBookForReadResult> {
  const { data, needUpdate } = await loadAndParseKieStory(projectId);

  const projectStore = useProjectStore();
  projectStore.isFree = data.isFree;

  if (!data.project) {
    throw new Error('工程数据为空，无法阅读');
  }

  // 1. 无需写回：视为已转存完成
  if (!needUpdate) {
    return { mode: 'published', data };
  }

  // 2. 尝试写回：成功=已转存；IMAGE_GENERATING=未转存完
  const persist = await tryPersistProjectConfig(projectId, data);
  if (persist === 'generating') {
    return { mode: 'preview', data };
  }

  return { mode: 'published', data };
}

/** 拉取并解析书本配置；不写回后端。 */
async function loadAndParseKieStory(
  projectId: string
): Promise<{ data: ProjectConfigData; needUpdate: boolean }> {
  // https://test2.yunzhan365.com/_aipicturebook/dev_jihj/qasn/_meat/config.json
  const res: any = await getBook(projectId);

  const userStore = useUserStore();

  if (!res.data) throw new Error(`加载故事数据失败: ${res.status}`);

  const content: string = res.data.config;
  const raw: ProjectConfigData = JSON.parse(await decryptProjectConfig(content));

  // raw.project = null;

  raw.isFree = userStore.isFreeUser;
  let needUpdate = false;
  const bookUserId = getBookUserId(res.data);
  if (bookUserId !== undefined && raw.userId !== bookUserId) {
    raw.userId = bookUserId;
    needUpdate = true;
  }

  if (!raw.project) {
    const baseUrl = res.data.url.replace('/index.html', '');
    raw.project = await parseKieStoryToProjectData(raw, baseUrl, projectId, true);
    raw.project.baseUrl = baseUrl;
    raw.project.shareUrl = res.data.url;
    //更新上去的数据，图片链接确保是相对路径
    needUpdate = true;
  } else {
    const baseUrl = res.data.url.replace('/index.html', '');
    if (raw.project.baseUrl !== baseUrl) {
      raw.project.baseUrl = baseUrl;
      needUpdate = true;
    }
    if (raw.project.shareUrl !== res.data.url) {
      raw.project.shareUrl = res.data.url;
      needUpdate = true;
    }
  }

  if (res.data.title && raw.project.name !== res.data.title) {
    raw.project.name = res.data.title;
  }

  hydrateProjectTTSAudioSplits(raw.project, raw.tts);

  // 开发 / 测试环境打印解密后的配置，便于排查解析问题
  if (import.meta.env.MODE === 'dev' || import.meta.env.MODE === 'test') {
    console.log('response', res.data);
    console.log('raw', raw);
  }

  //同步水印状态
  await userStore.refreshMeta();
  const enable_watermark = userStore.userMeta.enable_watermark ?? DEFAULT_ENABLE_WATERMARK;
  if (raw.project.enable_watermark !== enable_watermark) {
    raw.project.enable_watermark = enable_watermark;
  }

  return { data: raw, needUpdate };
}

/** 是否图片生成/转存中导致的不可编辑错误 */
function isImageGeneratingError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    String((error as Record<string, unknown>).code ?? '') === 'IMAGE_GENERATING'
  );
}

/**
 * 尝试写回工程配置。
 * @returns ok=写回成功；generating=转存未完成（可走预览兜底）
 */
async function tryPersistProjectConfig(
  projectId: string | number,
  data: ProjectConfigData
): Promise<'ok' | 'generating'> {
  try {
    await updateBook(projectId, data);
    return 'ok';
  } catch (error: unknown) {
    if (isImageGeneratingError(error)) {
      console.warn('图片生成中，暂不写回工程配置', error);
      return 'generating';
    }
    throw error;
  }
}

/**
 * 阅读端加载与 index.html 同级的 project-config.json（加密工程配置）。
 */
export async function fetchLocalProjectConfig(): Promise<ProjectConfigData> {
  const { data, uiConfig } = await fetchPublishedProjectConfig();

  if (import.meta.env.MODE === 'dev' || import.meta.env.MODE === 'test') {
    console.log('response', data);
    // raw.project = null;
  }

  const projectStore = useProjectStore();
  projectStore.isFree = data.isFree;

  // 步骤 3：本地配置可能仅有 story，现场解析为工程数据（与 CDN 拉取逻辑一致）
  if (!data.project) {
    data.isFree = true;
    projectStore.isFree = data.isFree;

    // 获取当前 HTML 页面的父级目录完整 URL
    const parentDir = new URL('..', window.location.href).href;
    data.project = await parseKieStoryToProjectData(data, parentDir, String(data.id));
    data.project.baseUrl = parentDir;

    // 更新书本数据（生成中不可编辑时软失败）
    await tryPersistProjectConfig(data.id, data);
  }

  applyPublishedUiConfig(data, uiConfig);
  hydrateProjectTTSAudioSplits(data.project, data.tts);

  return data;
}

function getBookUserId(book: {
  ownerId?: unknown;
  authorId?: unknown;
  userId?: unknown;
}): number | undefined {
  return (
    normalizeUserId(book.ownerId) ?? normalizeUserId(book.authorId) ?? normalizeUserId(book.userId)
  );
}

function normalizeUserId(value: unknown): number | undefined {
  const userId = Number(value);
  return Number.isFinite(userId) && userId > 0 ? userId : undefined;
}

/**
 * 有 url 但时长缺失/为 0 时，探测音频元数据并写回毫秒时长。
 */
async function ensureTtsDuration(ttsItem: TTSAudioData, baseUrl: string): Promise<void> {
  // 1. 无路径，或已有有效时长，直接跳过
  if (!ttsItem.url || (ttsItem.duration && ttsItem.duration > 0)) return;

  // 2. 相对路径拼成绝对 URL 后再探测（返回秒），写回毫秒
  const url = getAbsolutePath(baseUrl, ttsItem.url);
  // console.log('url', url);
  const seconds = await getAudioDurationFromUrl(url);
  ttsItem.duration = seconds > 0 ? Math.round(seconds * 1000) : 0;
}

/** 并行补全 TTS 列表中缺失的时长。 */
async function ensureTTSDurations(tts: KieStoryTTSData | null, baseUrl: string): Promise<void> {
  if (!tts?.audio?.length) return;
  await Promise.all(tts.audio.map((item) => ensureTtsDuration(item, baseUrl)));
}

/**
 * 将 kie 故事 JSON 解析为 ProjectData
 * 步骤1：计算书页尺寸
 * 步骤2：补全 TTS 缺失时长（有 url 且 duration 无效时同步探测）
 * 步骤3：封面（pages[0]）占单页
 * 步骤4：其余故事页各拆成 left + right 两条 BookPageData
 */
export async function parseKieStoryToProjectData(
  projectConfig: ProjectConfigData,
  baseUrl: string,
  projectId?: string,
  useAppFonts = false
): Promise<ProjectData> {
  const { id, story, tts, music } = projectConfig;

  const { story: storyMeta } = story;

  // 兼容新结构，优先从 storyMeta 中取
  const cover = storyMeta.cover ?? story.cover;
  const layout = storyMeta.layout ?? story.layout;
  const page_aspect_ratio = storyMeta.page_aspect_ratio ?? story.page_aspect_ratio;
  const language = storyMeta.language_code ?? 'zh';

  const resolvedLayout = resolveLayoutType(layout ?? '');
  let pageRatio = normalizePageAspectRatio(page_aspect_ratio);
  if (resolvedLayout === LAYOUT_TYPE.FULL_SCREEN) {
    pageRatio = pageRatio / 2;
  }

  if (tts) {
    // 有路径但时长缺失/为 0 时，并行探测并写回
    await ensureTTSDurations(tts, baseUrl);
  }

  const { width: pageW, height: pageH } = parsePageDimensions(pageRatio);

  cover.narration = storyMeta.title ?? '';

  const pages: BookPageData[] = buildBookPages(
    resolvedLayout,
    language,
    pageW,
    pageH,
    cover,
    storyMeta.pages,
    tts,
    projectConfig.isFree
  );

  const bgm = music
    ? createSoundActionData(music.url, music.name, {
        volume: music.volume / 100,
        loop: true,
        isBgm: true
      })
    : undefined;

  //读取一下默认字体信息（不注册），为了获取模板的字体信息
  await fontManager.loadDefaultFonts();
  const fontTextMap = fontManager.getFontTextMapFromPages(pages);
  const fonts = await fontManager.processFontSubset(fontTextMap, projectId, useAppFonts);

  return {
    id,
    name: storyMeta.title,
    version: '1.0.0',
    layout: resolvedLayout,
    bgm,
    book: {
      id: `book-${nanoid(6)}`,
      title: storyMeta.title,
      width: pageW,
      height: pageH,
      pages
    },
    fonts
  };
}

function buildBookPages(
  layoutType: LayoutType,
  language: string,
  pageW: number,
  pageH: number,
  coverPage: KieStoryPage,
  storyPages: KieStoryPage[],
  tts: KieStoryTTSData | null,
  /** 免费用户尾页显示二维码，付费用户不显示 */
  showQrCodeOnTailPage: boolean
): BookPageData[] {
  const pages: BookPageData[] = [];

  const { cover: ttsCover, list: ttsList } = getTTSAudioMetaList(tts);

  if (coverPage) {
    coverPage.hasDuration = true;
    coverPage.tts = ttsCover;
    pages.push(
      createBookPageData(pageW, pageH, coverPage, true, {
        type: 'cover',
        layoutType: 'cover',
        pageIndex: 0,
        originIndex: 0,
        pageW,
        pageH,
        language,
        key: `page_${nanoid(6)}_0`
      })
    );
  }

  let pageIndex = pages.length;
  storyPages.forEach((storyPage, index) => {
    // 初始化旁白音频数据
    storyPage.tts = ttsList[index] ?? null;
    storyPage.hasDuration = true;

    const datas = [{ ...storyPage }];
    const originIndex = index + 1;

    if (layoutType === LAYOUT_TYPE.FULL_SCREEN) {
      datas.push({ ...storyPage });
      // 跨页时随机选择一页显示 narration，另一页不显示
      const randomIndex = Math.floor(Math.random() * datas.length);
      // 没有旁白页不需要时长，所有时长分给了另一页
      datas[randomIndex].narration = '';
      datas[randomIndex].tts = null;
      datas[randomIndex].hasDuration = false;
    }

    if (layoutType === LAYOUT_TYPE.TEXT_IMAGE_34) {
      datas.push({ ...storyPage });
      datas[0].image_url = '';
      /** 左页纯文，跳过插画创建，防止显示插画生成失败提示 */
      datas[0].omitImage = true;
      // 没有旁白页不需要时长，所有时长分给了另一页
      datas[1].narration = '';
      datas[1].tts = null;
      datas[1].hasDuration = false;
    }

    // 跨页配对 key：同一 spread 的页共享相同 key
    const spreadKey = `page_${nanoid(6)}_${originIndex}`;

    datas.forEach((pageData) => {
      pages.push(
        createBookPageData(pageW, pageH, pageData, true, {
          type: 'normal',
          layoutType,
          pageIndex: pageIndex,
          originIndex,
          pageW,
          pageH,
          language,
          key: spreadKey
        })
      );

      pageIndex++;
    });
  });

  const tailPageBuilder = new KieStoryTailPageDataBuilder(pageW, pageH, showQrCodeOnTailPage);
  if (tailPageBuilder.shouldAppend(pages.length)) {
    pages.push(tailPageBuilder.build(pages.length));
  }

  return pages;
}

export function createBookPageData(
  pageW: number,
  pageH: number,
  storyPage: KieStoryPage,
  needSpineShadow: boolean,
  options: {
    type: BookPageData['type'];
    layoutType: LayoutType | 'cover';
    pageIndex: number;
    originIndex: number;
    pageW: number;
    pageH: number;
    language: string;
    key?: string;
  }
): BookPageData {
  const pageId = `page-${nanoid(6)}`;

  const hasTtsAudio = Boolean(storyPage.tts?.url);
  // TTS 元数据使用毫秒，页面和 Action 时间线统一使用秒。
  const ttsDuration = hasTtsAudio ? (storyPage.tts?.duration ?? DEFAULT_PAGE_DURATION) / 1000 : 0;

  const duration = hasTtsAudio
    ? TTS_AUDIO_LEADING_PAUSE_DURATION + ttsDuration + TTS_AUDIO_TRAILING_PAUSE_DURATION
    : 0.1;

  const actions = buildContentActionDatas(
    options.layoutType,
    options.language,
    pageW,
    pageH,
    storyPage,
    options.pageIndex,
    options.key
  );

  actions.forEach((action) => {
    if (action.type === ACTION_TYPE.SOUND) {
      // 声音在前停顿结束后开始，只占用实际 TTS 播放时长。
      action.startTime = TTS_AUDIO_LEADING_PAUSE_DURATION;
      action.duration = ttsDuration;
      return;
    }
    action.duration = duration;
  });

  const pageSide: PageSide =
    options.type === 'cover' ? 'none' : options.pageIndex % 2 === 1 ? 'left' : 'right';

  return {
    id: pageId,
    key: options.key,
    needSpineShadow: needSpineShadow,
    side: pageSide,
    type: options.type,
    index: options.pageIndex,
    originIndex: options.originIndex,
    width: options.pageW,
    height: options.pageH,
    originWidth: options.pageW,
    originHeight: options.pageH,
    startTime: 0,
    duration,
    tts: storyPage.tts,
    actions,
    aiMeta: {
      scene_description: storyPage.scene_description
    }
  };
}

/** 按单侧布局配置生成 actions */
function buildContentActionDatas(
  layoutType: LayoutType | 'cover',
  language: string = 'zh',
  pageW: number,
  pageH: number,
  storyPage: KieStoryPage,
  index: number = 0,
  pageKey?: string
): ActionData[] {
  const { image: imageLayout, text: textLayout, textStyle } = getLayoutConfig(layoutType, language);

  const actions: ActionData[] = [];

  // 步骤1：处理插画
  // - omitImage=true：跳过 url 判空检查，不创建插画（如 TEXT_IMAGE_34 纯文侧）
  // - 否则：对 url 判空；空 url 仍创建 IMAGE，由 ImageElement → permanent →「页面插画生成失败」
  if (imageLayout && !storyPage.omitImage) {
    const imageUrl = (storyPage.image_url ?? '').trim();

    let side: PageSide = 'none';
    if (layoutType === LAYOUT_TYPE.FULL_SCREEN && index !== 0) {
      side = index % 2 === 1 ? 'left' : 'right';
    }

    const action = createImageActionData(imageUrl, {
      x: imageLayout.x! * pageW,
      y: imageLayout.y! * pageH,
      width: imageLayout.width! * pageW,
      height: imageLayout.height! * pageH,
      layout: {
        parentWidth: pageW,
        parentHeight: pageH,
        x: imageLayout.x,
        y: imageLayout.y,
        width: imageLayout.width,
        height: imageLayout.height,
        left: imageLayout.left,
        top: imageLayout.top,
        right: imageLayout.right,
        bottom: imageLayout.bottom
      }
    });

    const config: ElementConfig = {};

    if (side !== 'none') config.crop = side;
    if (imageLayout.isFull) config.fullscreen = true;

    action.element!.config = config;

    if (pageKey) {
      action.element!.key = pageKey;
    }

    actions.push(action);
  }

  if (textLayout && storyPage.narration) {
    let narration = storyPage.narration;
    let finalTextStyle = textStyle;

    // 封面标题自适应字号与换行
    if (layoutType === 'cover') {
      ({ narration, textStyle: finalTextStyle } = applyCoverTextAdaptive(
        narration,
        language,
        textLayout.width! * pageW,
        textStyle
      ));
    }

    const action = createTextActionData(narration, finalTextStyle, {
      x: textLayout.x! * pageW,
      y: textLayout.y! * pageH,
      width: textLayout.width! * pageW,
      height: textLayout.height! * pageH,
      layout: {
        parentWidth: pageW,
        parentHeight: pageH,
        x: textLayout.x,
        y: textLayout.y,
        width: textLayout.width,
        height: textLayout.height,
        left: textLayout.left,
        top: textLayout.top,
        right: textLayout.right,
        bottom: textLayout.bottom
      }
    });

    actions.push(action);
  }

  if (storyPage.tts && storyPage.tts.url) {
    const action = createSoundActionData(
      storyPage.tts.url,
      '配音',
      {
        volume: (storyPage.tts.volume ?? 100) / 100
      },
      { ...storyPage.tts }
    );
    actions.push(action);
  }

  return actions;
}
